import React, { useState } from "react";
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import {
    DataGridPremium,
    GridColDef,
    GridToolbar,
} from "@mui/x-data-grid-premium";

import { FiChevronDown } from "react-icons/fi";

interface NumericDistributionEntry {
    year: number;
    product_level_1: string | null;
    distinct_clients: number;
    total_clients: number;
    numeric_distribution: number;
}

interface NumericDistributionTableProps {
    data: NumericDistributionEntry[];
}

const capitalizeFirstLetter = (label: string): string => {
    if (!label) return "";
    return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
};

const BarChartInTable: React.FC<{
    value: number;
}> = ({ value }) => {
    const percentage = Math.min(Math.max(value, 0), 100); // Clamp between 0-100
    return (
        <Box sx={{ position: "relative", width: "100%", height: "10px" }}>
            <Box
                sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: `${percentage}%`,
                    height: "100%",
                    bgcolor: "#d3d3d3",
                    borderRadius: "5px",
                }}
            />
            <Typography
                variant="caption"
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: `50%`,
                    transform: "translate(-50%, -50%)",
                    fontSize: "0.65rem",
                    whiteSpace: "nowrap",
                    zIndex: 1,
                }}
            >
                {percentage.toFixed(0)}%
            </Typography>
        </Box>
    );
};

const NumericDistributionTable: React.FC<NumericDistributionTableProps> = ({
    data,
}) => {
    // Group by product_level_1
    const grouped: Record<
        string,
        { [year: number]: NumericDistributionEntry }
    > = {};

    console.log("data", data);

    const sortedData = [...data].sort(
        (a, b) => b.numeric_distribution - a.numeric_distribution
    );

    sortedData.forEach((entry) => {
        const key = entry.product_level_1 || "Inconnu";
        if (!grouped[key]) grouped[key] = {};
        grouped[key][entry.year] = entry;
    });

    // const rows = sortedData.map((item, index) => ({
    //     id: index,
    //     label: capitalizeFirstLetter(item.label),
    //     bar: item.value,
    //     value: formatNumber(item.value),
    //     change: formatNumber(item.change),
    // }));

    const rows = Object.entries(grouped).map(([product, yearMap], idx) => ({
        id: idx,
        product_level_1: capitalizeFirstLetter(product),
        distinct_clients_2024: yearMap[2024]?.distinct_clients || 0,
        distinct_clients_2025: yearMap[2025]?.distinct_clients || 0,
        total_clients_2024: yearMap[2024]?.total_clients || 0,
        total_clients_2025: yearMap[2025]?.total_clients || 0,
        numeric_distribution_2024:
            yearMap[2024]?.numeric_distribution?.toFixed(2) || "0.00",
        numeric_distribution_2025:
            yearMap[2025]?.numeric_distribution?.toFixed(2) || "0.00",
    }));

    const columns: GridColDef[] = [
        { field: "product_level_1", headerName: "Catégorie", flex: 1 },
        // {
        //     field: "distinct_clients_2024",
        //     headerName: "Clients 2024",
        //     type: "number",
        //     flex: 1,
        // },
        // {
        //     field: "distinct_clients_2025",
        //     headerName: "Clients 2025",
        //     type: "number",
        //     flex: 1,
        // },
        // {
        //     field: "total_clients_2024",
        //     headerName: "Total 2024",
        //     type: "number",
        //     flex: 1,
        // },
        // {
        //     field: "total_clients_2025",
        //     headerName: "Total 2025",
        //     type: "number",
        //     flex: 1,
        // },
        // {
        //     field: "numeric_distribution_2024",
        //     headerName: "Distribution % 2024",
        //     flex: 1,
        // },
        {
            field: "numeric_distribution_2025",
            headerName: "Distribution % 2025",
            flex: 1,
            renderCell: (params) => <BarChartInTable value={params.value} />,
        },
    ];

    const minHeight = 50;
    const maxHeight = 120;

    const [expanded, setExpanded] = useState<boolean>(true);

    return (
        // <Box sx={{ height: 600, width: "100%", mt: 2 }}>

        <Accordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            disableGutters
            elevation={0}
            square
            sx={{
                mb: 1,
                boxShadow: "none",
                border: "none",
                "&::before": {
                    display: "none", // removes the default divider line
                },
            }}
        >
            <AccordionSummary
                expandIcon={<FiChevronDown />}
                sx={{
                    p: 0, // removes all padding
                    m: 0, // removes all margin
                    minHeight: 0,
                    "& .MuiAccordionSummary-content": {
                        m: 0,
                        p: 0,
                    },
                    "& .MuiAccordionSummary-expandIconWrapper": {
                        p: 0, // optional: tighten up the expand icon padding
                        m: 0,
                    },
                }}
            >
                <Typography
                    variant="subtitle2"
                    fontSize="0.875rem"
                    sx={{ fontWeight: "bold" }}
                >
                    Détention catégories produits
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        maxHeight,
                        minHeight,
                    }}
                >
                    <DataGridPremium
                        rowHeight={30}
                        rows={rows}
                        columns={columns}
                        disableRowSelectionOnClick
                        slots={{
                            columnHeaders: () => null,
                            toolbar: GridToolbar,
                        }}
                        hideFooter
                        disableColumnMenu
                        density="compact"
                        // slots={{
                        //     columnHeaders: () => null,
                        // }}
                        sx={{
                            overflow: "hidden",
                            border: "none",
                            "& .MuiDataGrid-root": {
                                border: "none",
                            },
                            "& .MuiDataGrid-columnHeaders": {
                                borderBottom: "none",
                            },
                            "& .MuiDataGrid-cell": {
                                fontSize: "0.75rem",
                                padding: "1px",
                                borderBottom: "none",
                                borderTop: "none",
                                textAlign: "left",
                                display: "flex",
                                alignItems: "center",
                            },
                            "& .positive-change": {
                                color: "#009A0A",
                            },
                            "& .negative-change": {
                                color: "#FF365E",
                            },
                            // Hover to show scrollbar
                            "&:hover": {
                                "&::-webkit-scrollbar": {
                                    opacity: 1, // Make scrollbar visible on hover
                                },
                            },
                            "&::-webkit-scrollbar": {
                                width: "8px", // Width of the scrollbar
                                opacity: 0, // Initially hide the scrollbar
                                transition: "opacity 0.3s", // Smooth transition
                            },
                            "&::-webkit-scrollbar-track": {
                                background: "#f1f1f1", // Light track color
                            },
                            "&::-webkit-scrollbar-thumb": {
                                background: "#888", // Scrollbar thumb color
                                // borderRadius: "4px", // Rounded corners for the scrollbar thumb
                            },
                            "&::-webkit-scrollbar-thumb:hover": {
                                background: "#555", // Hover effect for the scrollbar thumb
                            },
                        }}
                    />
                </div>
            </AccordionDetails>
        </Accordion>
    );
};

export default NumericDistributionTable;
